// Winners vs losers grouped by hold-time bucket. Reveals whether you cut
// winners early or hold losers too long — the two cardinal sins.

import { useQuery } from '@tanstack/react-query';
import { SectionCard } from './ui/SectionCard';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtMoney } from '@/lib/format';

export function HoldingPeriods() {
  const q = useQuery({ queryKey: ['holding-periods'], queryFn: api.holdingPeriods });
  const data = (q.data?.buckets ?? []).map((b) => ({
    label: b.label,
    winners: b.winners,
    losers: -b.losers, // bars below the x-axis
    avgWinR: b.avgWinR,
    avgLossR: b.avgLossR,
  }));

  return (
    <SectionCard>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-indigo-300" />
          <h3 className="text-sm font-medium text-zinc-200">Holding periods</h3>
        </div>
        <p className="text-[11px] text-zinc-400">Winners ↑ vs losers ↓ by hold time</p>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fill: '#a1a1aa', fontSize: 11 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#71717a', fontSize: 11 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={{ stroke: '#27272a' }}
              width={24}
              allowDecimals={false}
              tickFormatter={(v) => String(Math.abs(v))}
            />
            <Tooltip
              contentStyle={{
                background: '#0a0a0f',
                border: '1px solid #26262f',
                borderRadius: 8,
                fontSize: 11,
              }}
              labelStyle={{ color: '#a1a1aa' }}
              itemStyle={{ color: '#e4e4e7' }}
              formatter={(v: number, name) => {
                const abs = Math.abs(v);
                return [`${abs} ${abs === 1 ? 'trade' : 'trades'}`, name];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, color: '#71717a' }}
              iconType="square"
            />
            <Bar dataKey="winners" name="Winners" fill="#34d399" isAnimationActive={false} />
            <Bar dataKey="losers" name="Losers" fill="#fb7185" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-7 gap-1.5 mt-2 text-[10px]">
        {(q.data?.buckets ?? []).map((b) => (
          <div
            key={b.label}
            className="rounded border border-zinc-800 bg-zinc-900/30 px-1.5 py-1"
          >
            <div className="text-zinc-400">{b.label}</div>
            <div className="num text-emerald-400">+{fmtMoney(b.avgWinR).replace('£', '')}</div>
            <div className="num text-rose-400">{fmtMoney(b.avgLossR).replace('£', '')}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
